import type { ReactNode } from "react";
import { SlideHeader } from "../../../SlideHeader";

// TODO(thomas/yevhen): replace with the public sample-audit URL before the
// deck is shared. Left as "#" so the placeholder is obvious in-app.
const EXAMPLE_AUDIT_URL = "#";

interface QA {
  q: string;
  a: ReactNode;
}

const QUESTIONS: QA[] = [
  {
    q: "You promise more organic installs. How do you prove the platform moves that number?",
    a: (
      <>
        Conversion and visibility deltas on the customer's own ASC / Play
        Console data, tied via{" "}
        <span className="text-slate-900">Score History</span> to the change
        that moved them. Causal attribution is a pilot or geo-holdout question
        — never claimed from the dashboard.
      </>
    ),
  },
  {
    q: "Don't paid attribution tools — AppsFlyer, Adjust, Branch — already do this?",
    a: (
      <>
        They attribute installs to <span className="text-slate-900">ad
        campaigns</span>; we attribute listing changes to{" "}
        <span className="text-slate-900">organic conversion and visibility</span>.
        Different question, different surface — the two coexist.
      </>
    ),
  },
  {
    q: "If AI coding lowered the barrier to build here, what stops others from competing?",
    a: (
      <>
        A demo is easy; operating a production ASO platform{" "}
        <span className="text-slate-900">every day</span> is not. Incumbents
        won't rebuild on legacy stacks; new entrants ship thin wrappers, not
        the system.
      </>
    ),
  },
  {
    q: "Won't a frontier lab — OpenAI, Anthropic, Google — just launch this?",
    a: (
      <>
        ASO is <span className="text-slate-900">venture-scale for a focused
        startup</span>, immaterial on a lab's P&amp;L. They're likelier the
        model layer beneath us than a competitor.
      </>
    ),
  },
  {
    q: "What's the biggest risk over the next year?",
    a: (
      <>
        A credible competitor emerging — someone pairing real ASO judgment with
        an AI-native build. We think it's unlikely at speed: the combination is
        rare and our head start compounds weekly. But it's the risk we watch.
      </>
    ),
  },
  {
    q: "You built an agentic software factory — why not just buy one?",
    a: (
      <>
        The vendors selling one — Factory, Augment — sell a{" "}
        <span className="font-medium text-slate-900">multiplier</span> for an
        engineering org you already staff, and report 3–5×. Ours{" "}
        <span className="font-medium text-slate-900">replaces</span> that org:
        it is why engineering is ~9% of the operating plan and not 40%+. It
        isn't the product you're funding — it's why the product costs what it
        does.
      </>
    ),
  },
  {
    q: "What's the corporate and ownership structure?",
    a: (
      <>
        <span className="text-slate-900">Delaware C-Corp</span> via Stripe
        Atlas on standard Cooley docs. Thomas ~50%, Yevhen ~35%, remainder in
        the option pool.
      </>
    ),
  },
  {
    q: "Can I see an example audit report?",
    a: (
      <>
        Yes —{" "}
        <a
          href={EXAMPLE_AUDIT_URL}
          target="_blank"
          rel="noreferrer"
          className="font-semibold text-blue-700 underline decoration-blue-300 underline-offset-2 hover:decoration-blue-700"
        >
          view a full sample audit
        </a>{" "}
        generated by Apptonomy on a live store listing.
      </>
    ),
  },
];

export default function CommonQuestions() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:pl-24 md:pr-16 md:pt-16 md:pb-10 md:[&_header]:mb-5">
      <SlideHeader unnumbered eyebrow="Appendix A · Common Questions" />

      <div className="min-h-0 md:flex-1 md:columns-2 md:gap-5">
        {QUESTIONS.map((qa) => (
          <QACard key={qa.q} {...qa} />
        ))}
      </div>
    </div>
  );
}

function QACard({ q, a }: QA) {
  return (
    <div className="mb-3.5 break-inside-avoid rounded-xl bg-white p-4 ring-1 ring-slate-200 md:mb-4">
      <p className="text-[15px] font-semibold leading-snug text-slate-900 md:text-[17px]">
        {q}
      </p>
      <div className="mt-2 text-xs leading-relaxed text-slate-600 md:text-[13px]">
        {a}
      </div>
    </div>
  );
}
